import type { AxiosResponse } from "axios";

import { deleteData, getData, postData } from "@/apis/apiInstance";

import type {
  AddGstRegisterBusinessPayloadType,
  AddIdPasswordManagerPayloadType,
  ApplyLeaveFormType,
  DeleteIdPasswordManagerPayloadType,
  DeleteUpcomingLeavePayload,
  GetClientIdPasswordManagerListPayloadType,
  GetGenerateSasTokenPayloadType,
  GetIdPasswordManagerListPayloadType,
  IsGstRegisteredVerifyPayloadType,
  LeaveAndWFHListPayloadType,
  LeaveAndWFHStatusPayload,
  SingleApplyPayload,
  UpdateIdPasswordManagerPayloadType,
  UploadFilePayloadType,
} from "@/commonTypes/stcoToolsType";

import { getCSRFToken } from "@/utils/commonFunctions";

export async function getIdPasswordManagerApi(
  data: GetIdPasswordManagerListPayloadType
): Promise<AxiosResponse> {
  const {
    token,
    searchData,
    limit,
    offset,
    clientCode,
    accessMemberId,
    category,
    attachments,
  } = data;
  return await getData(`id-password/record/list`, {
    params: {
      search_term: searchData,
      limit: limit,
      offset: offset,
      client_code: clientCode,
      category,
      access_member_id: accessMemberId,
      attachments: attachments,
    },
    headers: {
      "user-token": token,
      "Content-Type": "application/json",
    },
  });
}

export async function addIdPasswordManagerApi(
  data: AddIdPasswordManagerPayloadType
): Promise<AxiosResponse> {
  const {
    token,
    title,
    clientCode,
    clientName,
    url,
    password,
    comments,
    category,
    loginId,
    accessMemberIds,
    targetAudiences,
    clientShow,
    uploads,
  } = data;

  const finalData = {
    show_client: clientShow,
    title: title?.trim(),
    client_code: clientCode,
    client_name: clientName?.trim(),
    url: url?.trim(),
    password: password?.trim(),
    comments: comments?.trim(),
    category: category?.trim(),
    login_id: loginId?.trim(),
    access_member_ids: accessMemberIds,
    target_audiences: targetAudiences?.trim(),
    uploads: uploads,
  };
  return await postData(`id-password/record/create`, finalData, {
    headers: {
      "user-token": token,
      "Content-Type": "application/json",
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function deleteIdPasswordManagerApi(
  data: DeleteIdPasswordManagerPayloadType
): Promise<AxiosResponse> {
  const { token, record_id } = data;
  return await deleteData(`/id-password/record/delete`, {
    headers: {
      "user-token": token,
      "Content-Type": "application/json",
      "x-csrf-token": getCSRFToken(),
    },
    params: {
      record_id: record_id,
    },
  });
}

export async function editIdPasswordManagerApi(
  data: UpdateIdPasswordManagerPayloadType
): Promise<AxiosResponse> {
  const {
    token,
    recordId,
    title,
    clientCode,
    clientName,
    url,
    password,
    comments,
    category,
    loginId,
    accessMemberIds,
    targetAudiences,
    clientShow,
    uploads,
  } = data;
  const finalData = {
    show_client: clientShow,
    title: title?.trim(),
    record_id: recordId,
    client_code: clientCode,
    client_name: clientName?.trim(),
    url: url?.trim(),
    password: password?.trim(),
    login_id: loginId?.trim(),
    comments: comments?.trim(),
    category: category,
    access_member_ids: accessMemberIds,
    target_audiences: targetAudiences?.trim(),
    uploads: uploads,
  };
  return await postData(`id-password/record/update`, finalData, {
    headers: {
      "user-token": token,
      "Content-Type": "application/json",
      "x-csrf-token": getCSRFToken(),
    },
  });
}

// Client Id Password Manager

export async function getClientIdPasswordManagerApi(
  data: GetClientIdPasswordManagerListPayloadType
): Promise<AxiosResponse> {
  const {
    token,
    searchData,
    client_code,
    limit,
    offset,
    category,
    accessMemberId,
    attachments,
  } = data;
  return await getData(`id-password/record/client-list`, {
    params: {
      client_code,
      search_term: searchData,
      limit,
      offset,
      category,
      access_member_id: accessMemberId,
      attachments,
    },
    headers: {
      "user-token": token,
      "Content-Type": "application/json",
    },
  });
}

export async function addClientIdPasswordManagerApi(
  data: AddIdPasswordManagerPayloadType
): Promise<AxiosResponse> {
  const {
    token,
    title,
    clientCode,
    clientName,
    url,
    password,
    comments,
    category,
    loginId,
    accessMemberIds,
    targetAudiences,
    clientShow,
    uploads,
  } = data;

  const finalData = {
    show_client: clientShow,
    title: title?.trim(),
    client_code: clientCode,
    client_name: clientName?.trim(),
    url: url?.trim(),
    password: password?.trim(),
    comments: comments?.trim(),
    category: category?.trim(),
    login_id: loginId?.trim(),
    access_member_ids: accessMemberIds,
    target_audiences: targetAudiences?.trim(),
    uploads: uploads,
  };
  return await postData(`id-password/record/client-create`, finalData, {
    headers: {
      "user-token": token,
      "Content-Type": "application/json",
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function editClientIdPasswordManagerApi(
  data: UpdateIdPasswordManagerPayloadType
): Promise<AxiosResponse> {
  const {
    token,
    recordId,
    title,
    clientCode,
    clientName,
    url,
    password,
    comments,
    category,
    loginId,
    accessMemberIds,
    targetAudiences,
    clientShow,
    uploads,
  } = data;
  const finalData = {
    show_client: clientShow,
    title: title?.trim(),
    record_id: recordId,
    client_code: clientCode,
    client_name: clientName?.trim(),
    url: url?.trim(),
    password: password?.trim(),
    login_id: loginId?.trim(),
    comments: comments?.trim(),
    category: category,
    access_member_ids: accessMemberIds,
    target_audiences: targetAudiences?.trim(),
    uploads: uploads,
  };
  return await postData(`id-password/record/client-update`, finalData, {
    headers: {
      "user-token": token,
      "Content-Type": "application/json",
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function getLeaveHistoryListApi(
  data: LeaveAndWFHListPayloadType
): Promise<AxiosResponse> {
  const {
    token,
    limit,
    offset,
    user_type,
    status,
    type,
    isPageSeen,
    redirectNotificationId,
    userId,
  } = data;
  return await getData(`finac-crm/employee-availability/list`, {
    params: {
      user_type,
      limit,
      offset,
      status,
      type,
      is_page_seen: isPageSeen,
      notification_id: redirectNotificationId,
      user_id: userId,
    },
    headers: {
      "user-token": token,
      "Content-Type": "application/json",
    },
  });
}

export async function addApplyLeaveOrWHFApi(
  data: ApplyLeaveFormType
): Promise<AxiosResponse> {
  const {
    token,
    user_id,
    start_date,
    end_date,
    leave_type,
    approved_by,
    people_to_notify,
    reason,
    file_attachment,
    leave,
    total_days,
    type,
  } = data;
  const finalData = {
    user_id,
    start_date,
    end_date,
    leave_type,
    approved_by: approved_by?.value,
    people_to_notify,
    reason,
    file_attachment,
    leave,
    total_days,
    type,
  };
  return await postData(`finac-crm/employee-availability/add`, finalData, {
    headers: {
      "user-token": token,
      "Content-Type": "application/json",
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function getSingleLeaveApi(
  data: SingleApplyPayload
): Promise<AxiosResponse> {
  const {
    token,
    employee_availability_id,
    isPageSeen,
    redirectNotificationId,
  } = data;
  return await getData(`finac-crm/employee-availability`, {
    params: {
      employee_availability_id,
      is_page_seen: isPageSeen,
      notification_id: redirectNotificationId,
    },
    headers: {
      "user-token": token,
      "Content-Type": "application/json",
    },
  });
}

export async function updateApplyLeaveOrWHFApi(
  data: ApplyLeaveFormType
): Promise<AxiosResponse> {
  const {
    token,
    user_id,
    start_date,
    end_date,
    leave_type,
    approved_by,
    people_to_notify,
    reason,
    file_attachment,
    leave,
    _id,
    total_days,
    request_status,
    type,
  } = data;
  const finalData = {
    user_id,
    start_date,
    end_date,
    leave_type,
    approved_by: approved_by?.value,
    people_to_notify,
    reason,
    file_attachment,
    leave,
    _id,
    total_days,
    request_status,
    type,
  };
  return await postData(`finac-crm/employee-availability/update`, finalData, {
    headers: {
      "user-token": token,
      "Content-Type": "application/json",
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function deleteUpcomingLeaveApi(
  data: DeleteUpcomingLeavePayload
): Promise<AxiosResponse> {
  const { token, id } = data;

  return await deleteData(`finac-crm/employee-availability/delete`, {
    params: {
      id,
    },
    headers: {
      "user-token": token,
      "Content-Type": "application/json",
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function leaveStatusChangeApi(
  data: LeaveAndWFHStatusPayload
): Promise<AxiosResponse> {
  const { token, employee_availability_id, request_status } = data;
  let finalData = {
    employee_availability_id,
    request_status,
  };
  return await postData(
    `finac-crm/employee-availability/update-status`,
    finalData,
    {
      headers: {
        "user-token": token,
        "Content-Type": "application/json",
        "x-csrf-token": getCSRFToken(),
      },
    }
  );
}

export async function addGstRegisterBusinessApi(
  data: AddGstRegisterBusinessPayloadType
): Promise<AxiosResponse> {
  const { token, gstin_number, gst_userName, gst_password, sub_org_name } =
    data;

  const finalData = { gstin_number, gst_userName, gst_password, sub_org_name };
  return await postData(`master-india/gst/register/business`, finalData, {
    headers: {
      "user-token": token,
      "Content-Type": "application/json",
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function isGstRegisteredVerifyApi(
  data: IsGstRegisteredVerifyPayloadType
): Promise<AxiosResponse> {
  const { token, client_code, gst_number } = data;
  return await getData(`id-password/check/gst-number/registration`, {
    params: {
      client_code,
      gst_number,
    },
    headers: {
      "user-token": token,
      "Content-Type": "application/json",
    },
  });
}

// upload file api
export async function uploadFilesApi(
  filesData: UploadFilePayloadType
): Promise<any> {
  const { token, files, containerType } = filesData;

  if (!files || !Array.isArray(files)) {
    throw new Error("Invalid files data: Expected an array of files.");
  }

  const formData = new FormData();
  formData.append("container_type", containerType);
  files.forEach((file: File) => {
    formData.append("files", file);
  });

  return await postData("finac/blob/upload", formData, {
    headers: {
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function getTokenGenerateForDownloadApi(
  data: GetGenerateSasTokenPayloadType
): Promise<AxiosResponse> {
  const { token, fileUrl, containerType, clientCode } = data;
  return await getData(`finac/generate-sas-token`, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
    params: {
      file_url: fileUrl,
      container_type: containerType,
      client_code: clientCode,
    },
  });
}
